import React from "react"
import { useTranslation } from "react-i18next"

import { useAuth } from "hooks/useAuth"
import FormElement from "components/Form/FormElement"
import { jsonHash } from "utils/hash"

import {
    OCODE_GENDERS_LIST,
    ADMIN_BICYCODE,
    ADMIN_MFC,
    ADMIN_MOUSTACHE,
    MC_OPERATOR,
    BC_OPERATOR,
    MFC_OPERATOR
} from "utils/constants"
import { CheckOutlineIcon } from "components/Elements/Icons"

export const OperatorsForm = ({
    partner,
    setUpdatedPartner,
    setIsMFCSelected,
    errors,
    setErrors,
    isEdit,
    isViewMode,
    editCallback,
    showLocalErrors,
    setShowLocalErrors
}) => {
    const [formFields, setFormFields] = React.useState({})
    const defaultFormEvents = React.useMemo(() => ({
        ready: true,
        // when form is in edition mode, by default the editable is set at false
        // when the form is not in edition mode, it means creation mode, the form should be editable
        editable: !isEdit,
        viewMode: isViewMode || false
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }), [isEdit])
    const [formEvents, setFormEvents] = React.useState(defaultFormEvents)
    const formHasChanged = jsonHash(partner) !== jsonHash(formFields)

    const { t } = useTranslation()
    const { user } = useAuth()

    const isAdminOCode = OCODE_GENDERS_LIST.includes(user?.gender)
    const isAdminBicycode = user?.gender === ADMIN_BICYCODE
    const isAdminMFC = user?.gender === ADMIN_MFC
    const isAdminMC = user?.gender === ADMIN_MOUSTACHE

    React.useEffect(() => {
        // When partner reloaded (after await fetchData()), set data in formFields
        if(partner) setFormFields(partner)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [partner])

    React.useEffect(() => {
        // When we create or update a partner, we have to pass data to parent directly.
        // We don't have any submit button on this component in this case
        setUpdatedPartner((prevFormFields) => ({
            ...prevFormFields,
            isBicycode: formFields.isBicycode,
            isMFC: formFields.isMFC,
            isMoustache: formFields.isMoustache,
        }))
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [formFields])

    React.useEffect(() => {
        // If isMFC is selected, we pass boolean to PartnerBO to display <ServicesForm /> component
        setIsMFCSelected(formFields.isMFC)
    },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [formFields.isMFC])

    const toggleEditableForm = (event) => {
        event.preventDefault()
        setFormEvents({
            ...formEvents,
            editable: !formEvents.editable
        })
    }

    const handleInputChange = React.useCallback(
        (event) => {
            const target = event.target
            const value = target.type === "checkbox" ? target.checked : target.value
            const name = target.name
            setFormFields(s => ({
                ...s,
                [name]: value
            }))
        }, []
    )

    const handleValidation = React.useCallback((name, errs) => {
        setErrors((s) => {
            const cleanErrors = [...s.filter((e) => e.origin !== name)]
            return [
                ...cleanErrors,
                ...errs
            ]
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const handleSubmit = async (event) => {
        event.preventDefault()
        // Validate inputs
        const formReady = !(errors.length > 0)

        if (!formReady) {
            setShowLocalErrors(true)
        }
        else {
            // hide error
            setShowLocalErrors(false)
            // prevent form to be submitted again
            setFormEvents(s => ({
                ...s,
                ready: false
            }))

            const updatedPartner = {
                ...formFields,
                isBicycode: formFields.isBicycode,
                isMFC: formFields.isMFC,
                isMoustache: formFields.isMoustache,
            }

            await editCallback(updatedPartner)

            setFormEvents(s => ({
                ...s,
                ready: true,
                editable: false
            }))
        }
    }

    const formElementCommonProps = {
        showErrors: showLocalErrors,
        onValidate: handleValidation,
        onChange: handleInputChange,
    }

    return (
        <form className="">
            <section className="c-card p-5 md:p-12 mb-7">

                <h2 className="c-h2 font-semibold mb-2 uppercase">
                    {t("edit.operator_for", { ns: "partner" })} *
                </h2>

                <div className="l-grid">
                    {(isAdminOCode || isAdminBicycode) &&
                        <div className="p-5">
                            <FormElement
                                {...formElementCommonProps}
                                value={formFields.isBicycode}
                                name="isBicycode"
                                type="checkbox"
                                image={BC_OPERATOR}
                                checked={isAdminBicycode}
                                className="py-4"
                                classNameOption="flex-col items-center"
                                disabled={!formEvents.editable || (!isAdminOCode && isEdit)}
                            />
                            {
                                formFields.isBicycode && formFields.is_activated_by_futurosoft && formFields.is_activated_by_futurosoft.is_activate_by_G8 &&
                                <p className="flex justify-center mt-5"><span className="mr-1">{t("futurosoft_activate", { ns: "partner" })}</span><CheckOutlineIcon size={24} /></p>
                            }
                        </div>
                    }

                    {(isAdminOCode || isAdminMFC) &&
                        <div className="p-5">
                            <FormElement
                                {...formElementCommonProps}
                                value={formFields.isMFC}
                                name="isMFC"
                                type="checkbox"
                                image={MFC_OPERATOR}
                                checked={isAdminMFC}
                                className="py-4"
                                classNameOption="flex-col items-center"
                                disabled={!formEvents.editable || (!isAdminOCode && isEdit)}
                            />
                        </div>
                    }

                    {(isAdminOCode || isAdminMC) &&
                        <div className="p-5">
                            <FormElement
                                {...formElementCommonProps}
                                value={formFields.isMoustache}
                                name="isMoustache"
                                type="checkbox"
                                image={MC_OPERATOR}
                                checked={isAdminMC}
                                className="py-4"
                                classNameOption="flex-col items-center"
                                disabled={!formEvents.editable || (!isAdminOCode && isEdit)}
                            />
                        </div>
                    }           
                </div>

                {isEdit && !isViewMode && !isAdminBicycode &&
                    <div className="flex justify-end mt-7">
                        {formEvents.editable
                            ?
                            <>
                                <button
                                    type="button"
                                    className="c-btn c-btn--white-bordered c-btn--m px-12 mx-5"
                                    onClick={toggleEditableForm}
                                >
                                    {t("global.cancel")}
                                </button>
                                <button
                                    type="submit"
                                    className="c-btn c-btn--primary c-btn--m px-12 mx-5"
                                    onClick={handleSubmit}
                                    disabled={!formEvents.ready || !formHasChanged}
                                >
                                    {t("global.save")}
                                </button>
                            </>
                            :
                            <button
                                type="button"
                                className="c-btn c-btn--primary px-12 mx-5"
                                onClick={toggleEditableForm}
                            >
                                {t("global.update")}
                            </button>
                        }
                    </div>
                }
            </section>
        </form>
    )
}
